import { type GetServerSideProps, type InferGetServerSidePropsType } from 'next';
import { NextSeo } from 'next-seo';
import { getRequestPromise } from '@qb/httpRequest/RequestPromise';
import { HUBSPOT_ACCESS_TOKEN } from '@/WebConstants';
import { AllBlogPostsResponse, BlogAuthorResponse, BlogPostResponse } from '@/shared/types/common/BlogTypes';
import { BlogPost } from '@/src/components/Blog/BlogPost/BlogPost';
import { getReadTimeFromHtml } from '@/src/components/Blog/BlogPost/utils/getReadTimeFromHtml';
type Post = BlogPostResponse & {
  postReadTime: number;
  postAuthor: BlogAuthorResponse;
};
const BLOG_API_URL = 'https://api.hubapi.com/cms/v3/blogs';
const BLOG_API_REQ_HEADERS = {
  headers: {
    Authorization: `Bearer ${HUBSPOT_ACCESS_TOKEN}`,
    'Content-Type': 'application/json'
  }
};
const BlogPostPage = ({
  post
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  if (post) {
    return <>
        <NextSeo defaultTitle={post.htmlTitle} description={post.metaDescription} />
        <BlogPost post={post} />
      </>;
  } else {
    return null;
  }
};
export default BlogPostPage;
export const getServerSideProps: GetServerSideProps<{
  post: Post;
}> = async context => {
  const slug = context.query.slug;
  if (!slug) {
    console.warn(`No slug provided for fetching blog page`);
    return {
      notFound: true
    };
  }
  const currentPostSummary = await getRequestPromise<AllBlogPostsResponse>(`${BLOG_API_URL}/posts?slug=blog/${slug}`, BLOG_API_REQ_HEADERS);
  if (!(currentPostSummary?.total > 0)) {
    console.warn(`No currentPostSummary for slug: ${slug}`);
    return {
      notFound: true
    };
  }
  const currentPost = await getRequestPromise<BlogPostResponse>(`${BLOG_API_URL}/posts/${currentPostSummary.results[0].id}`, BLOG_API_REQ_HEADERS);
  if (!currentPost || !currentPost.blogAuthorId) {
    if (!currentPost) {
      console.warn(`No currentPost for slug: ${slug}`);
    }
    if (!currentPost.blogAuthorId) {
      console.warn(`No currentPost.blogAuthorId for slug: ${slug}`);
    }
    return {
      notFound: true
    };
  }
  const postAuthor = await getRequestPromise<BlogAuthorResponse>(`${BLOG_API_URL}/authors/${currentPost.blogAuthorId}`, BLOG_API_REQ_HEADERS);
  if (!postAuthor) {
    console.warn(`No postAuthor for slug: ${slug}`);
  }
  const postReadTime = getReadTimeFromHtml(currentPost.postBody);
  if (!postReadTime) {
    console.warn(`No postReadTime for slug: ${slug}`);
  }
  return {
    props: {
      post: {
        ...currentPost,
        postReadTime,
        postAuthor
      }
    }
  };
};